import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import client from '../api/client';

/**
 * PermissionContext
 *
 * Resolves the logged-in user's effective permission set by reading from
 * GET /api/admin/role-permissions (the same data source that the backend
 * requirePermission() middleware uses), so frontend and backend always agree.
 *
 * Rules (mirroring requirePermission.js):
 *   • role === 'admin' | 'superadmin' | 'owner' → always allowed (no fetch)
 *   • role in customRoles → use that role's permissions[]
 *   • role NOT in customRoles (bootstrap) → fall back to DEFAULT_ROLE_PERMS
 *   • permissions includes 'full_access' → always allowed
 *
 * Cache note: the backend invalidates its 1-minute in-process cache after a PUT
 * to /api/admin/role-permissions.  The frontend re-fetches on every navigation
 * (location.pathname change) AND whenever the user object changes (role / school /
 * id).  Navigation-triggered re-fetches are silent background updates — they do
 * not set loading:true, so the sidebar never flickers on a normal page switch.
 * Only the first fetch for a given user session shows the loading state.
 */

const PermissionContext = createContext({
  hasPermission:    () => true,
  hasAnyPermission: () => true,
  loading: false,
});

/** Roles that unconditionally pass every permission check. */
const BYPASS_ROLES = new Set(['admin', 'superadmin', 'owner']);

/** Roles that have no admin permissions — skip the admin API call entirely. */
const NON_STAFF_ROLES = new Set(['parent', 'student']);

/**
 * Fallback permission sets for each role when the school has never saved
 * custom permissions (bootstrap state).  Mirrors DEFAULT_ROLES in UsersRoles.js
 * so a fresh install's cashier still sees Fees and a registrar sees Students.
 */
const DEFAULT_ROLE_PERMS = {
  cashier:   ['record_payments', 'view_fees', 'send_payment_links'],
  registrar: ['create_students', 'edit_student_records', 'enroll_students',
              'transfer_students', 'manage_parent_links', 'view_academic_records'],
  teacher:   ['take_attendance', 'enter_grades', 'view_class_students', 'send_announcements'],
};

export function PermissionProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();

  /** Set<permKey> | null.  null means "full bypass" (admin/superadmin/owner). */
  const [perms,   setPerms]   = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Track the last user-identity key we fetched for.
   * When this changes (new login / role change) we show the loading state.
   * When only the pathname changes (same user navigating around) we do a
   * silent background refresh so there is no sidebar flicker.
   */
  const lastUserKey = useRef(null);

  useEffect(() => {
    // No user (logged out) → nothing to do
    if (!user) {
      setPerms(null);
      setLoading(false);
      lastUserKey.current = null;
      return;
    }

    // Bypass roles never need a fetch
    if (BYPASS_ROLES.has(user.role)) {
      setPerms(null); // null signals "full bypass" — hasPermission always returns true
      setLoading(false);
      lastUserKey.current = null;
      return;
    }

    const userKey = `${user._id}:${user.role}:${user.school_id}`;

    // Parent / student roles have no admin permissions — skip the admin API call
    if (NON_STAFF_ROLES.has(user.role)) {
      setPerms(new Set());
      setLoading(false);
      lastUserKey.current = userKey;
      return;
    }

    // Determine whether this is the first fetch for this user session or a
    // background navigation refresh.  Only the first fetch shows the loading
    // spinner — background refreshes update perms silently once the response
    // arrives, preventing any sidebar flicker on normal navigation.
    const isNewSession = lastUserKey.current !== userKey;
    lastUserKey.current = userKey;

    if (isNewSession) setLoading(true);

    client.get('/admin/role-permissions')
      .then(({ data }) => {
        const customRoles = Array.isArray(data?.customRoles) ? data.customRoles : [];
        const myRoleData  = customRoles.find(r => r.key === user.role);

        // Prefer saved perms; fall back to hardcoded defaults for bootstrap safety
        const permArray = (myRoleData?.permissions?.length > 0)
          ? myRoleData.permissions
          : (DEFAULT_ROLE_PERMS[user.role] || []);

        setPerms(new Set(permArray));
      })
      .catch(() => {
        // Network error on a navigation refresh → keep existing perms rather than
        // downgrading access.  Only fall back to defaults on the initial session
        // fetch when there are no perms yet.
        if (isNewSession) {
          setPerms(new Set(DEFAULT_ROLE_PERMS[user.role] || []));
        }
      })
      .finally(() => {
        if (isNewSession) setLoading(false);
      });
  // Re-fetch when user identity changes (login / role change) OR on every
  // navigation so an admin's permission edits are picked up without a logout.
  }, [user?.role, user?._id, user?.school_id, location.pathname]);

  /**
   * hasPermission(permKey)
   *
   * Returns true if the current user is allowed to perform the action identified
   * by permKey.  A null/undefined permKey means "no restriction" → always true.
   */
  const hasPermission = (permKey) => {
    if (!permKey) return true;           // no restriction declared
    if (!user)    return false;          // not authenticated
    if (BYPASS_ROLES.has(user.role)) return true;  // admin / superadmin / owner
    if (!perms || !(perms instanceof Set)) return false;
    return perms.has('full_access') || perms.has(permKey);
  };

  /**
   * hasAnyPermission(...permKeys)
   *
   * Returns true if the user has at least one of the listed permission keys.
   * Mirrors backend's requireAnyPermission().
   */
  const hasAnyPermission = (...keys) => {
    if (!keys.length) return true;
    return keys.some(k => hasPermission(k));
  };

  return (
    <PermissionContext.Provider value={{ hasPermission, hasAnyPermission, loading }}>
      {children}
    </PermissionContext.Provider>
  );
}

export const usePermissions = () => useContext(PermissionContext);
